import {
  WellnessMetricDto,
  WellnessMetricValuesDto,
  WellnessQuestionDto,
} from '@repo/shared/dtos/analytics/WellnessDto';

export type WellnessQuestionResult = WellnessQuestionDto & WellnessMetricValuesDto;

export const calculateCompletionRate = (completed: number, total: number): number =>
  total > 0 ? (completed / total) * 100 : 0;

export const getShortLabel = (question: Pick<WellnessQuestionDto, 'shortlabel' | 'label'>) =>
  question.shortlabel || question.label;

export const getImprovementColor = (
  higherIsBetter: boolean,
  improvementPercentage: number,
): string => {
  if (!higherIsBetter) {
    return improvementPercentage < 0 ? 'text-green-600' : 'text-red-600';
  }
  return improvementPercentage >= 0 ? 'text-green-600' : 'text-red-600';
};

export const findBestMetricImprovement = (metricsData: WellnessMetricDto) => {
  const improvements = metricsData.questions
    .flatMap(question => {
      const actual = question.improvement_percentage;
      if (actual === null) return [];
      return [
        {
          name: getShortLabel(question),
          questionkey: question.questionkey,
          effectiveImprovement: question.higherisbetter ? actual : -actual,
          actual,
        },
      ];
    })
    .filter(item => item.effectiveImprovement > 0);

  if (improvements.length === 0) {
    return { name: 'No Data', questionkey: null as string | null, actual: 0 };
  }

  return improvements.reduce((prev, current) =>
    current.effectiveImprovement > prev.effectiveImprovement ? current : prev,
  );
};

export interface WellnessMetricDisplay {
  id: string;
  metric: string;
  pre: number | null;
  post: number | null;
  improvement: number | null;
  improvementPercentage: number | null;
  question: WellnessQuestionResult;
}

export function transformWellnessMetrics(data: WellnessMetricDto | null): WellnessMetricDisplay[] {
  if (!data) return [];

  return data.questions.map(question => ({
    id: question.questionkey,
    metric: getShortLabel(question),
    pre: question.avg_pre !== null ? Number(question.avg_pre.toFixed(3)) : null,
    post: question.avg_post !== null ? Number(question.avg_post.toFixed(3)) : null,
    improvement: question.improvement,
    improvementPercentage: question.improvement_percentage,
    question,
  }));
}

export function formatWellnessExportData(data: WellnessMetricDto | null) {
  if (!data) return [];

  return data.questions.map(question => ({
    metric: getShortLabel(question),
    avgPre: question.avg_pre,
    avgPost: question.avg_post,
    improvement: question.improvement,
    improvementPercentage:
      question.improvement_percentage !== null
        ? `${question.improvement_percentage.toFixed(1)}%`
        : 'N/A',
  }));
}

export const getImprovementDisplay = (percentage: number) => {
  const sign = percentage > 0 ? '+' : '';
  const arrow = percentage > 0 ? '↗' : percentage < 0 ? '↘' : '→';
  return { sign, arrow, value: percentage.toFixed(1) };
};
